import React, { useRef, useEffect } from 'react';

interface ReCaptchaComponentProps {
  sitekey: string;
  setToken: (token: string) => void;
  onWidgetIdSet?: (widgetId: number) => void;
  action?: string;
}

const ReCaptchaComponent: React.FC<ReCaptchaComponentProps> = ({
  sitekey,
  setToken,
  onWidgetIdSet,
  action,
}) => {
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);

  useEffect(() => {
    const renderRecaptcha = () => {
      if (
        window.grecaptcha?.enterprise?.render &&
        recaptchaContainerRef.current &&
        widgetIdRef.current === null
      ) {
        try {
          const renderOptions: any = {
            sitekey,
            callback: (token: string) => {
              setToken(token);
            },
            'expired-callback': () => {
              setToken('');
            },
            theme: 'light',
          };

          if (action) {
            renderOptions.action = action;
          }

          widgetIdRef.current = window.grecaptcha.enterprise.render(
            recaptchaContainerRef.current,
            renderOptions
          );

          if (onWidgetIdSet && widgetIdRef.current !== null) {
            onWidgetIdSet(widgetIdRef.current);
          }
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error);
        }
      }
    };

    // Wait for grecaptcha to be ready
    if (window.grecaptcha?.enterprise?.ready) {
      window.grecaptcha.enterprise.ready(renderRecaptcha);
    } else if (window.grecaptcha?.ready) {
      window.grecaptcha.ready(renderRecaptcha);
    } else {
      // Fallback: wait for script to load
      (window as any).onRecaptchaLoad = renderRecaptcha;
    }

    // Cleanup on unmount
    return () => {
      if (window.grecaptcha?.enterprise && widgetIdRef.current !== null) {
        try {
          window.grecaptcha.enterprise.reset(widgetIdRef.current);
        } catch (error) {
          console.error('Error resetting reCAPTCHA:', error);
        }
      }
      // Clear token on unmount
      setToken('');
    };
  }, [sitekey, setToken, onWidgetIdSet, action]);

  return <div ref={recaptchaContainerRef} />;
};

export default ReCaptchaComponent;
